import { useEffect, useState } from "react";
import { X } from "lucide-react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
} from "@mui/material";

export const MARKET_NOTICE_STORAGE_KEY = "oxy-market-notice-uso-indevido-marca";

const noticeParagraphs = [
  "A OXY DISTRIBUIDORA DE TÍTULOS E VALORES MOBILIÁRIOS S.A. informa que identificou uma página fraudulenta que utiliza indevidamente seu nome e sua identidade visual, com o objetivo de se passar por um canal oficial da instituição.",
  "Esclarecemos que a referida página não possui qualquer vínculo com a OXY DTVM e não é autorizada a representá-la ou a realizar comunicações em seu nome.",
  "Diante disso, recomendamos a clientes, parceiros e demais interessados que não interajam com a página, não forneçam dados pessoais ou financeiros e não realizem pagamentos, transferências ou quaisquer operações solicitadas por esse canal.",
  "A instituição já está adotando as medidas cabíveis para apuração dos fatos e remoção da página fraudulenta, bem como para resguardar seus públicos e sua identidade institucional.",
  "Reforçamos a importância de verificar a autenticidade dos canais de comunicação antes de compartilhar informações ou realizar qualquer operação. A OXY DTVM não utiliza contas de WhatsApp ou canais de comunicação alternativos diferentes daqueles oficiais divulgados em suas páginas institucionais.",
  "Em caso de dúvida, entre em contato com a OXY DTVM exclusivamente por seus canais oficiais.",
];

function hasSeenNotice() {
  try {
    return window.sessionStorage.getItem(MARKET_NOTICE_STORAGE_KEY) === "1";
  } catch {
    return false;
  }
}

function markNoticeAsSeen() {
  try {
    window.sessionStorage.setItem(MARKET_NOTICE_STORAGE_KEY, "1");
  } catch {
    // Sem storage disponível o aviso volta a aparecer no próximo acesso.
  }
}

export function MarketNoticeDialog() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (!hasSeenNotice()) {
      setIsOpen(true);
    }
  }, []);

  const handleClose = () => {
    markNoticeAsSeen();
    setIsOpen(false);
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="market-notice-title"
      PaperProps={{
        sx: {
          borderRadius: 3.5,
          width: "100%",
          mx: 2,
          boxShadow: "none",
          border: "1px solid var(--oxy-border)",
        },
      }}
    >
      <DialogContent sx={{ px: { xs: 2.6, sm: 3.2 }, pt: { xs: 2.4, sm: 3 }, pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box>
            <Typography
              sx={{
                fontSize: "0.82rem",
                fontWeight: 700,
                letterSpacing: "0.08em",
                color: "var(--oxy-primary)",
              }}
            >
              COMUNICADO AO MERCADO
            </Typography>
            <Typography
              id="market-notice-title"
              component="h2"
              sx={{
                mt: 0.6,
                fontSize: { xs: "1.4rem", sm: "1.6rem" },
                fontWeight: 700,
                color: "var(--oxy-text-primary)",
                lineHeight: 1.3,
              }}
            >
              Alerta sobre uso indevido da marca OXY DTVM
            </Typography>
          </Box>

          <IconButton
            onClick={handleClose}
            aria-label="Fechar comunicado"
            sx={{ color: "var(--oxy-text-secondary)", mt: -0.6, mr: -0.6 }}
          >
            <X size={22} />
          </IconButton>
        </Box>

        {noticeParagraphs.map((paragraph) => (
          <Typography
            key={paragraph}
            sx={{ mt: 2, fontSize: "0.98rem", color: "var(--oxy-text-strong)", lineHeight: 1.7 }}
          >
            {paragraph}
          </Typography>
        ))}

        <Typography sx={{ mt: 2.4, fontSize: "0.95rem", fontWeight: 700, color: "var(--oxy-text-primary)" }}>
          OXY DTVM
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2.6, sm: 3.2 }, pb: { xs: 2.4, sm: 3 }, pt: 1.5 }}>
        <Button
          onClick={handleClose}
          variant="contained"
          autoFocus
          sx={{
            bgcolor: "var(--oxy-primary)",
            color: "var(--oxy-surface)",
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            px: 3.2,
            minHeight: 44,
            boxShadow: "none",
            "&:hover": {
              bgcolor: "var(--oxy-primary-dark)",
              boxShadow: "none",
            },
          }}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
  );
}
